import React, { useMemo, useState } from "react";
import { CrewPilotModuleShell } from "../CrewPilotModuleShell";
import { PAGE_LAYOUT } from "../../layout/pageLayout";
import { getVisibleAdminSections } from "../../helpers/adminSectionAccess";
import { usePermissions } from "../../hooks/usePermissions";
import { useIsDesktopViewport } from "../../hooks/useIsDesktopViewport";
import { DeskEmpty, DeskSearch, DeskToolbar } from "../desktop/DeskTable";
import "./adminDesktop.css";

// Same descriptions each section already shows under its own title.
const SECTION_BLURB = {
  organization: "Company profile and regional defaults",
  work_schedule: "Working days, hours per day and non-working dates",
  branding: "Logo for customer-facing reports",
  feature_flags: "Organization module toggles",
  subscription: "Plan and billing overview",
  users: "Employee profiles, manager tools, and user directory",
  invitations: "Invite members and manage pending invitations",
  trades: "Company trade catalog for payroll rate resolution",
  classifications: "Organization classification catalog",
  payroll_types: "Organization payroll type catalog",
  payroll_settings: "Payroll week and weekly overtime configuration",
  payroll: "Trade, classification, and payroll type rate matrix",
  work_types: "Work Types employees choose when they submit a session",
};

export function AdministrationOverviewScreen({ onSelectSection }) {
  const { can } = usePermissions();
  const [query, setQuery] = useState("");
  const isDesktop = useIsDesktopViewport();

  const sections = useMemo(
    () =>
      getVisibleAdminSections(can).filter((section) => section.id !== "overview"),
    [can],
  );

  const filteredSections = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) {
      return sections;
    }
    return sections.filter((section) =>
      section.label.toLowerCase().includes(normalized),
    );
  }, [query, sections]);

  if (isDesktop) {
    const groups = [];
    filteredSections.forEach((section) => {
      const name = section.group || "General";
      let group = groups.find((g) => g.name === name);
      if (!group) {
        group = { name, items: [] };
        groups.push(group);
      }
      group.items.push(section);
    });

    return (
      <CrewPilotModuleShell
        title="Administration"
        subtitle="Organization settings and configuration"
        layout={PAGE_LAYOUT.COMMAND}
        primary
      >
        <DeskToolbar>
          <DeskSearch
            value={query}
            onChange={setQuery}
            placeholder="Search administration sections"
            ariaLabel="Search administration sections"
            width="420px"
          />
          <span className="dt-toolbar__grow" />
          <span className="dt-summary">
            {filteredSections.length} section{filteredSections.length === 1 ? "" : "s"}
          </span>
        </DeskToolbar>

        {groups.map((group) => (
          <section key={group.name} className="adm-dirgroup" aria-label={group.name}>
            <h2>{group.name}</h2>
            <div className="adm-dir">
              {group.items.map((section) => (
                <button
                  key={section.id}
                  type="button"
                  className="adm-dir__item"
                  onClick={() => onSelectSection(section.id)}
                >
                  <span className="adm-dir__icon" aria-hidden="true">
                    {section.icon}
                  </span>
                  <span className="adm-dir__text">
                    <span className="adm-dir__title">{section.label}</span>
                    <span className="adm-dir__sub">{SECTION_BLURB[section.id] || group.name}</span>
                  </span>
                  <span className="adm-dir__chev" aria-hidden="true">
                    ›
                  </span>
                </button>
              ))}
            </div>
          </section>
        ))}

        {filteredSections.length === 0 && (
          <DeskEmpty title="No sections match your search" />
        )}
      </CrewPilotModuleShell>
    );
  }

  return (
    <CrewPilotModuleShell
      title="Administration"
      subtitle="Organization settings and configuration"
      sectionLabel="Sections"
      layout={PAGE_LAYOUT.COMMAND}
    >
      <div className="rb-card" style={{ marginBottom: "16px" }}>
        <div className="section-heading">FIND A SECTION</div>
        <input
          type="search"
          className="admin-search-input"
          placeholder="Search administration sections"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          aria-label="Search administration sections"
        />
      </div>

      <div className="quick-grid">
        {filteredSections.map((section) => (
          <button
            key={section.id}
            type="button"
            className="quick-card user-management-card"
            onClick={() => onSelectSection(section.id)}
          >
            <div className="quick-card-icon">{section.icon}</div>
            <div className="quick-card-title">{section.label}</div>
            {section.group ? (
              <div className="quick-card-subtitle">{section.group}</div>
            ) : null}
          </button>
        ))}
      </div>

      {filteredSections.length === 0 && (
        <div className="admin-placeholder-note">
          No sections match your search.
        </div>
      )}
    </CrewPilotModuleShell>
  );
}
